import type { PropsWithChildren } from "react";
import { createContext, useCallback, useMemo, useState } from "react";
import { getErrorMessage } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import CancelDialogIcon from "@entrilia/shared/icons/CancelDialogIcon";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import UserRemoveDialogIcon from "@entrilia/shared/icons/UserRemoveDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { saveCsvFile } from "@entrilia/shared/services/downloadFile";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../../api/adminApi";
import type {
  Company,
  OrganizationContactRole,
  OrganizationDeactivatedMember,
  OrganizationMember,
  OrganizationMemberInvitation,
} from "../../../../api/types/userManagementTypes";
import useClientContext from "../../../../context/useClientContext";
import useUserContext from "../../../../context/useUserContext";
import EmailSendDialogIcon from "@entrilia/shared/icons/EmailSendDialogIcon";
import EmailSuccessDialogIcon from "@entrilia/shared/icons/EmailSuccessDialogIcon";
import type { InfoDialogData } from "../../../common/InfoDialog";
import InfoDialog from "../../../common/InfoDialog";
import type { ConfirmMembersDialogData } from "./ConfirmMembersOperationDialog";
import ConfirmMembersOperationDialog from "./ConfirmMembersOperationDialog";
import type { MemberFilters, MembersTab } from "./definitions";
import { getSingleMembersOfOrgContactsRoles } from "./definitions";
import MemberDeactivationBlockedDialog from "./MemberDeactivationBlockedDialog";
import { getCsvToExport } from "./members-list/exportMembers";
import {
  getFilteredDeactivatedMembers,
  getFilteredMembers,
  getFilteredPendingInvites,
} from "./members-list/filters/filtering";

interface ContextValue {
  clientTitle: string;
  hasAccessToManageOrganizationUsers: boolean;
  hasAccessToManageOrganizationOwners: boolean;
  companies: Company[];
  companiesList: ListItemData[];
  filters: MemberFilters;
  setFilters: (filters: MemberFilters) => void;
  handleMembersSearch: (search: string) => void;
  members: OrganizationMember[];
  filteredMembers: OrganizationMember[];
  deactivatedMembers: OrganizationDeactivatedMember[];
  filteredDeactivatedMembers: OrganizationDeactivatedMember[];
  pendingInvites: OrganizationMemberInvitation[];
  filteredPendingInvites: OrganizationMemberInvitation[];
  updateMembers: () => Promise<void>;
  updatePendingInvites: () => Promise<void>;
  isLoading: boolean;
  fetchError: ApiError | undefined;
  activeToolbarTab: MembersTab;
  switchActiveToolbarTab: (tab: MembersTab) => void;
  selectedMemberIds: string[] | undefined;
  setSelectedMembersIds: (member: string[] | undefined) => void;
  deactivateMembers: (userIds: string[]) => void;
  clearFilters: () => void;
  deleteInvite: (invite: OrganizationMemberInvitation) => void;
  isSending: boolean;
  setIsSending: (isSending: boolean) => void;
  resendInvite: (invite: OrganizationMemberInvitation) => void;
  showInviteMembersDialog: boolean;
  inviteMembersDialogData: MemberInvitationDialogData | undefined;
  setInviteMembersDialogData: (inviteMembersDialogData: MemberInvitationDialogData | undefined) => void;
  openInviteMembersDialog: () => void;
  duplicateNewMembersFrom: (member: OrganizationMember) => void;
  manageAccess: (member: OrganizationMember) => void;
  exportToCsv: () => void;
  isCurrentUser: (idOrEmail: string) => boolean;
  currentUserData: [string, string];
}

interface MemberInvitationDialogData {
  emails?: string[];
  member?: OrganizationMember;
  action: "invite" | "manage";
}

interface MembersContextProviderProps {
  hasAccessToManageOrganizationUsers: boolean;
  hasAccessToManageOrganizationOwners: boolean;
}

interface MemberDeactivationBlockedDialogData {
  memberToDeactivate: OrganizationMember;
  orgContactRoles: OrganizationContactRole[];
  membersToContact: OrganizationMember[];
}

const MembersContext = createContext<ContextValue | undefined>(undefined);

export const MembersContextProvider = ({
  hasAccessToManageOrganizationUsers,
  hasAccessToManageOrganizationOwners,
  children,
}: PropsWithChildren<MembersContextProviderProps>) => {
  const { sendNotificationError } = useNotificationContext();
  const [isSending, setIsSending] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [filters, setFilters] = useState<MemberFilters>({
    search: "",
    types: [],
    role: undefined,
    companies: [],
  });
  const [activeToolbarTab, setActiveToolbarTab] = useState<MembersTab>("active");
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>();
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmMembersDialogData>();
  const [infoDialogData, setInfoDialogData] = useState<InfoDialogData>();
  const [inviteMembersDialogData, setInviteMembersDialogData] = useState<MemberInvitationDialogData>();
  const [deactivationBlockedDialogData, setDeactivationBlockedDialogData] =
    useState<MemberDeactivationBlockedDialogData>();

  const { title: clientTitle } = useClientContext();

  const { email: currentUserEmail, id: currentUserId } = useUserContext();

  const [companiesResp, companiesFetchError, { isFetching: isFetchingCompanies }] = useFetch(
    adminApi.getOrganizationManagedCompanies
  );

  const fetchMembers = useCallback(() => adminApi.getOrganizationMembers(), []);
  const [membersResp, membersFetchError, { isFetching: isFetchingMembers, setData: setMembers, fetch: updateMembers }] =
    useFetch(fetchMembers);

  const [deactivatedMembersResp, deactivatedMembersFetchError, { isFetching: isFetchingDeactivatedMembers }] = useFetch(
    adminApi.getOrganizationDeactivatedMembers
  );

  const [
    pendingInvitesResp,
    pendingInvitesFetchError,
    { isFetching: isFetchingPendingInvites, setData: setPendingInvites, fetch: updatePendingInvites },
  ] = useFetch(adminApi.getOrganizationMembersInvitations);

  const companies = useMemo(() => companiesResp?.companies ?? [], [companiesResp?.companies]);

  const members = useMemo(() => membersResp?.users ?? [], [membersResp?.users]);

  const singleMembersOfOrgContactsRoles = useMemo(() => getSingleMembersOfOrgContactsRoles(members), [members]);

  const deactivatedMembers = useMemo(
    () => deactivatedMembersResp?.deactivatedUsers ?? [],
    [deactivatedMembersResp?.deactivatedUsers]
  );

  const pendingInvites = useMemo(() => pendingInvitesResp?.invitations ?? [], [pendingInvitesResp?.invitations]);

  const filteredMembers = useMemo(
    () => (activeToolbarTab === "active" ? getFilteredMembers(members, filters) : members),
    [activeToolbarTab, filters, members]
  );

  const filteredDeactivatedMembers = useMemo(
    () =>
      activeToolbarTab === "deactivated"
        ? getFilteredDeactivatedMembers(deactivatedMembers, filters)
        : deactivatedMembers,
    [activeToolbarTab, deactivatedMembers, filters]
  );

  const filteredPendingInvites = useMemo(
    () => (activeToolbarTab === "pending" ? getFilteredPendingInvites(pendingInvites, filters) : pendingInvites),
    [activeToolbarTab, filters, pendingInvites]
  );

  const deactivateMembers = useCallback(
    async (memberIds: string[]) => {
      try {
        const results = await Promise.allSettled(
          memberIds.map((memberId) => adminApi.deleteOrganizationMember(memberId))
        );
        const failed = results.filter((result) => result.status === "rejected");
        if (failed.length > 0) {
          sendNotificationError(
            `Failed to deactivate ${failed.length} members. Please try again later or contact support.`
          );
        } else {
          setInfoDialogData({
            title: "Members Deactivated",
            description: `${memberIds.length} member${memberIds.length > 1 ? "s" : ""} successfully deactivated.`,
            icon: <UserRemoveDialogIcon sx={{ width: 80, height: 80 }} />,
          });
          setMembers({ users: (membersResp?.users ?? []).filter((member) => !memberIds.includes(member.userId)) });
        }
      } catch (error) {
        logError(error, "[MembersContext] deleteOrganizationMember");
        sendNotificationError("Failed to deactivate members: " + getErrorMessage(error));
      }
    },
    [membersResp, sendNotificationError, setMembers]
  );

  const companiesList = useMemo(() => {
    return (companiesResp?.companies ?? []).map((client) => ({
      label: client.title,
      value: client.clientCode,
      iconSrc: client.branding.logoMarkUrl,
    }));
  }, [companiesResp?.companies]);

  const switchActiveToolbarTab = (tab: MembersTab) => {
    setActiveToolbarTab(tab);
    clearFilters();
    setSelectedMemberIds(undefined);
  };

  const clearFilters = () => {
    setFilters({ search: filters.search, types: [], role: undefined, companies: [] });
  };

  const handleDeactivateMembers = (userIds: string[]) => {
    if (userIds.length === 0) {
      return;
    }

    const userIdThatIsSingleInContactRole = userIds.find((userId) => singleMembersOfOrgContactsRoles.has(userId));
    if (userIdThatIsSingleInContactRole) {
      const memberToDeactivate = members.find((member) => member.userId === userIdThatIsSingleInContactRole);
      if (!memberToDeactivate) {
        return;
      }

      const orgContactRoles = singleMembersOfOrgContactsRoles.get(userIdThatIsSingleInContactRole) ?? [];

      let membersToContact = members.filter((m) => m.organizationContactRoles.includes("PrimaryAccountContact"));
      if (membersToContact.some((m) => m.userId === currentUserId)) {
        membersToContact = [];
      }

      setDeactivationBlockedDialogData({
        memberToDeactivate,
        orgContactRoles,
        membersToContact,
      });
      return;
    }

    if (userIds.length === 1) {
      const selectedMember = members.find((member) => member.userId === userIds[0]);
      if (!selectedMember) {
        return;
      }

      setConfirmDialogData({
        title: "Deactivate this member?",
        subtitle: selectedMember.name,
        description: selectedMember.email,
        ids: userIds,
        confirmCallback: () => handleConfirmDeactivateMembers(userIds),
        confirmButtonColor: "error",
        confirmButtonLabel: "Deactivate",
        icon: <DeleteDialogIcon sx={{ width: 80, height: 80 }} />,
      });
      return;
    }

    setConfirmDialogData({
      title: "Deactivate these members?",
      description: `${userIds.length} members selected`,
      ids: userIds,
      confirmCallback: () => handleConfirmDeactivateMembers(userIds),
      confirmButtonColor: "error",
      confirmButtonLabel: "Deactivate",
      icon: <DeleteDialogIcon sx={{ width: 80, height: 80 }} />,
    });
  };

  const handleConfirmDeactivateMembers = useCallback(
    async (ids: string[]) => {
      if (ids) {
        setIsUpdating(true);
        await deactivateMembers(ids);
        setIsUpdating(false);
      }
      setConfirmDialogData(undefined);
    },
    [deactivateMembers]
  );

  const handleConfirmResendInvite = useCallback(
    async (invite: OrganizationMemberInvitation) => {
      setIsUpdating(true);
      try {
        const response = await adminApi.resendOrganizationUserInvite(invite.invitationId);
        setIsUpdating(false);
        if (response.success) {
          setInfoDialogData({
            title: "Invite resent",
            description: `Invite to ${invite.email} was successfully resent.`,
            icon: <EmailSuccessDialogIcon sx={{ width: 80, height: 80 }} />,
          });
          await updatePendingInvites();
        } else {
          sendNotificationError("Failed to resend invite.");
        }
      } catch (error) {
        logError(error, "MembersContext.handleConfirmResendInvite");
        sendNotificationError("Failed to resend invite: " + getErrorMessage(error));
      }
      setIsUpdating(false);
      setConfirmDialogData(undefined);
    },
    [sendNotificationError, updatePendingInvites]
  );

  const handleResendInvite = useCallback(
    (invite: OrganizationMemberInvitation) => {
      setConfirmDialogData({
        title: "Resend this invite",
        subtitle: invite.email,
        description: "Are you sure you want to resend this invite?",
        ids: [invite.invitationId],
        confirmCallback: () => handleConfirmResendInvite(invite),
        confirmButtonColor: "primary",
        confirmButtonLabel: "Resend",
        icon: <EmailSendDialogIcon sx={{ width: 80, height: 80 }} />,
      });
    },
    [handleConfirmResendInvite]
  );

  const handleConfirmDeleteInvite = useCallback(
    async (invite: OrganizationMemberInvitation) => {
      setIsUpdating(true);
      try {
        const response = await adminApi.deleteOrganizationUserInvite(invite.invitationId);
        setIsUpdating(false);
        if (response.success) {
          setInfoDialogData({
            title: "Invite invalidated",
            description: `${invite.email} invite successfully invalidated.`,
            icon: <CancelDialogIcon sx={{ width: 80, height: 80 }} />,
          });
          setPendingInvites({
            invitations: (pendingInvitesResp?.invitations ?? []).filter(
              (inv) => inv.invitationId !== invite.invitationId
            ),
          });
        } else {
          sendNotificationError("Failed to invalidate invite.");
        }
      } catch (error) {
        logError(error, "MembersContext.handleConfirmDeleteInvite");
        sendNotificationError("Failed to invalidate invite: " + getErrorMessage(error));
      }
      setIsUpdating(false);
      setConfirmDialogData(undefined);
    },
    [pendingInvitesResp, sendNotificationError, setPendingInvites]
  );

  const handleDeleteInvite = useCallback(
    (invite: OrganizationMemberInvitation) => {
      setConfirmDialogData({
        title: "Invalidate this invite",
        subtitle: invite.email,
        description: "Are you sure you want to invalidate this invite?",
        ids: [invite.invitationId],
        confirmCallback: () => handleConfirmDeleteInvite(invite),
        confirmButtonColor: "error",
        confirmButtonLabel: "Invalidate",
        icon: <CancelDialogIcon sx={{ width: 80, height: 80 }} />,
      });
    },
    [handleConfirmDeleteInvite]
  );

  const isCurrentUser = useCallback(
    (idOrEmail: string) =>
      idOrEmail.toLowerCase() === currentUserEmail.toLowerCase() ||
      idOrEmail.toLowerCase() === currentUserId.toLowerCase(),
    [currentUserEmail, currentUserId]
  );

  const openInvitationDialogAction = (action: "invite" | "manage", member?: OrganizationMember) =>
    setInviteMembersDialogData({
      action,
      member,
      emails: (membersResp?.users ?? [])
        .filter((member) => selectedMemberIds?.includes(member.userId))
        .map((member) => member.email),
    });

  const handleExportToCsv = () => {
    const csv = getCsvToExport(members, deactivatedMembers, companies);
    saveCsvFile(csv, `${clientTitle} Members as of ${formatDate(new Date())}.csv`);
  };

  const isLoading =
    isFetchingCompanies || isFetchingMembers || isFetchingDeactivatedMembers || isFetchingPendingInvites || isUpdating;

  const fetchError =
    companiesFetchError || membersFetchError || deactivatedMembersFetchError || pendingInvitesFetchError;

  return (
    <MembersContext
      value={{
        clientTitle,
        hasAccessToManageOrganizationUsers,
        handleMembersSearch: (search) => setFilters({ ...filters, search }),
        members,
        filteredMembers,
        deactivatedMembers,
        filteredDeactivatedMembers,
        pendingInvites,
        filteredPendingInvites,
        updateMembers,
        updatePendingInvites,
        isLoading,
        fetchError,
        activeToolbarTab,
        switchActiveToolbarTab,
        selectedMemberIds,
        setSelectedMembersIds: setSelectedMemberIds,
        deactivateMembers: handleDeactivateMembers,
        filters,
        setFilters,
        clearFilters,
        companies,
        companiesList,
        deleteInvite: handleDeleteInvite,
        isSending,
        setIsSending,
        resendInvite: handleResendInvite,
        inviteMembersDialogData,
        showInviteMembersDialog: inviteMembersDialogData !== undefined,
        setInviteMembersDialogData,
        manageAccess: (member: OrganizationMember) => openInvitationDialogAction("manage", member),
        duplicateNewMembersFrom: (member: OrganizationMember) => openInvitationDialogAction("invite", member),
        openInviteMembersDialog: () => openInvitationDialogAction("invite"),
        exportToCsv: handleExportToCsv,
        isCurrentUser,
        currentUserData: [currentUserId, currentUserEmail],
        hasAccessToManageOrganizationOwners,
      }}
    >
      {children}

      <ConfirmMembersOperationDialog
        open={confirmDialogData !== undefined}
        onClose={() => setConfirmDialogData(undefined)}
        onConfirm={() => {
          if (confirmDialogData && confirmDialogData.confirmCallback) {
            confirmDialogData.confirmCallback([...confirmDialogData.ids]);
          }
        }}
        data={confirmDialogData}
        isLoading={isLoading}
      />

      <InfoDialog isLoading={isLoading} data={infoDialogData} onClose={() => setInfoDialogData(undefined)} />

      {deactivationBlockedDialogData !== undefined && (
        <MemberDeactivationBlockedDialog
          onClose={() => setDeactivationBlockedDialogData(undefined)}
          memberToDeactivate={deactivationBlockedDialogData.memberToDeactivate}
          orgContactRoles={deactivationBlockedDialogData.orgContactRoles}
          membersToContact={deactivationBlockedDialogData.membersToContact}
        />
      )}
    </MembersContext>
  );
};

export default MembersContext;
